import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { subscribeToProducts, subscribeToInventory, unsubscribe } from '@/lib/realtime';

export interface Category {
  id: string;
  location_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  image_url: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  location_id: string;
  organization_id?: string | null;
  category_id: string | null;
  station_id: string | null; // Direct kitchen station assignment (overrides category-based routing)
  send_to_kitchen: boolean; // Whether this product is routed to KDS when ordered
  supplier_id: string | null; // Primary supplier for this product
  name: string;
  description: string | null;
  sku: string | null;
  sku_type: string | null;
  barcode: string | null;
  price: number;
  cost: number | null;
  tax_rate: number | null;
  image_url: string | null;
  is_active: boolean;
  is_available: boolean;
  is_sellable: boolean; // true = Sellable (appears on POS), false = Raw Goods (inventory only)
  track_inventory: boolean;
  low_stock_threshold: number | null;
  par_level: number | null;
  sort_order: number;
  product_type: 'item' | 'modifier' | 'bundle' | 'composite' | 'variant' | 'composite_variant';
  created_at: string;
  updated_at: string;
  product_locations?: Array<{
    location_id: string;
    is_available: boolean;
    price_override?: number | null;
    cost_override?: number | null;
    sku_override?: string | null;
    tax_rate_override?: number | null;
  }>;
  // Location-based pricing (computed at runtime, not stored in database)
  effective_price?: number; // Price after location overrides (use this for display/calculations)
  has_price_override?: boolean; // Whether this location has a custom price
}

export interface ProductLocationVisibility {
  id: string;
  product_id: string;
  location_id: string;
  is_visible: boolean;
  created_at: string;
}

export interface Location {
  id: string;
  name: string;
  organization_id: string;
}

export interface ProductWithCategory extends Product {
  category?: Category | null;
  location?: Location | null;
}

export interface LocationInventoryLevel {
  location_id: string;
  quantity_foh: number;  // Front of House (available to sell)
  quantity_boh: number;  // Back of House (storage)
  reorder_point?: number | null;
  reorder_quantity?: number | null;
}

export type CreateProductInput = Omit<Product, 'id' | 'created_at' | 'updated_at'> & {
  initial_stock?: number; // Optional initial stock quantity when creating with inventory tracking (legacy)
  inventory_levels?: LocationInventoryLevel[]; // Per-location inventory levels (new)
};
export type UpdateProductInput = Partial<Omit<Product, 'id' | 'created_at' | 'updated_at'>>;
export type CreateCategoryInput = Omit<Category, 'id' | 'created_at' | 'updated_at'>;
export type UpdateCategoryInput = Partial<Omit<Category, 'id' | 'created_at' | 'updated_at'>>;

export interface UseProductsOptions {
  includeHidden?: boolean; // Include products hidden from this location (for admin views)
  includePOSOnly?: boolean; // Only include products visible in POS
  locationId?: string | null; // Override location (for kiosk mode)
  allLocations?: boolean; // Fetch products from all accessible locations (for back office)
  accessibleLocationIds?: string[]; // List of location IDs user can access (required when allLocations is true)
}

export function useProducts(options: UseProductsOptions = {}) {
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();
  // Use provided locationId override, or fall back to store location
  const locationId = options.locationId !== undefined ? options.locationId : currentLocation?.id;
  const { includeHidden = false, includePOSOnly = true, allLocations = false, accessibleLocationIds = [] } = options;

  // Fetch product visibility settings
  const { data: visibilitySettings } = useQuery({
    queryKey: ['product-visibility', locationId],
    queryFn: async () => {
      if (!locationId) return [];

      const { data, error } = await supabase
        .from('product_location_visibility')
        .select('*')
        .eq('location_id', locationId);

      if (error) throw error;
      return data as ProductLocationVisibility[];
    },
    enabled: !!locationId && includePOSOnly && !includeHidden,
  });

  // Fetch products
  const {
    data: products,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['products', allLocations ? 'all' : locationId, includeHidden, allLocations ? accessibleLocationIds : null],
    queryFn: async () => {
      // Fetch all active products with location assignments, visibility, and price overrides
      const { data, error } = await supabase
        .from('products')
        .select('*, category:categories(*), location:locations(*), product_locations!left(location_id, is_available, price_override, cost_override, sku_override, tax_rate_override), product_location_visibility!left(*)')
        .eq('is_active', true)
        .order('location_id')
        .order('sort_order')
        .order('name');

      if (error) throw error;
      const rows = (data || []) as any[];

      const filterByLocations = (product: any, locationIds: string[]): boolean => {
        if (locationIds.length === 0) return true;

        // product_locations is the canonical multi-location assignment (source of truth).
        // When it has entries, use it exclusively to decide visibility.
        const productLocs = (product.product_locations || []) as { location_id: string; is_available: boolean }[];

        if (productLocs.length > 0) {
          const locAssigned = productLocs.find(pl => pl.location_id && locationIds.includes(pl.location_id));
          if (!locAssigned) return false;
          // Respect is_available flag -- if explicitly false, treat as hidden
          if (locAssigned.is_available === false && !includeHidden) return false;
          return true;
        }

        // Fallback for legacy products that have no product_locations rows yet:
        // 1. Home location match
        if (locationIds.includes(product.location_id)) return true;
        // 2. Legacy: product_location_visibility
        const vis = (product.product_location_visibility || []) as ProductLocationVisibility[];
        const visible = vis.find(v => v.location_id && locationIds.includes(v.location_id));
        if (!visible) return false;
        if (!visible.is_visible && !includeHidden) return false;
        return includeHidden || visible.is_visible;
      };

      let filteredProducts = rows.filter(p => {
        if (allLocations && accessibleLocationIds.length > 0) {
          return filterByLocations(p, accessibleLocationIds);
        }
        if (!allLocations && locationId) {
          return filterByLocations(p, [locationId]);
        }
        return true;
      }) as ProductWithCategory[];

      // Filter by visibility if not including hidden and we have visibility settings
      if (!includeHidden && visibilitySettings && visibilitySettings.length > 0) {
        const hiddenProductIds = new Set(
          visibilitySettings
            .filter(v => !v.is_visible)
            .map(v => v.product_id)
        );

        filteredProducts = filteredProducts.filter(p => !hiddenProductIds.has(p.id));
      }

      // Compute effective prices based on location overrides
      // For allLocations mode, we use the product's home location for price lookups
      // For single location mode, we check for overrides at the current location
      const productsWithEffectivePrices = filteredProducts.map(product => {
        const productLocations = ((product as any).product_locations || []) as Array<{
          location_id: string;
          price_override?: number | null;
        }>;
        // Determine which location to check for price override
        const targetLocationId = allLocations ? product.location_id : locationId;
        const override = productLocations.find(
          (pl) => pl.location_id === targetLocationId
        );
        return {
          ...product,
          effective_price: override?.price_override ?? product.price,
          has_price_override: override?.price_override != null,
        };
      });

      return productsWithEffectivePrices;
    },
    enabled: allLocations ? accessibleLocationIds.length > 0 : !!locationId,
  });

  // Fetch categories
  const {
    data: categories,
    isLoading: categoriesLoading,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ['categories', allLocations ? 'all' : locationId, allLocations ? accessibleLocationIds : null],
    queryFn: async () => {
      // All locations mode
      if (allLocations && accessibleLocationIds.length > 0) {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .in('location_id', accessibleLocationIds)
          .eq('is_active', true)
          .order('sort_order')
          .order('name');

        if (error) throw error;
        return data as Category[];
      }

      // Single location mode
      if (!locationId) return [];

      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('location_id', locationId)
        .eq('is_active', true)
        .order('sort_order')
        .order('name');

      if (error) throw error;
      return data as Category[];
    },
    enabled: allLocations ? accessibleLocationIds.length > 0 : !!locationId,
  });

  // Create product
  const createProduct = useMutation({
    mutationFn: async (input: CreateProductInput) => {
      // Extract inventory fields from input (not database columns)
      const { initial_stock, inventory_levels, ...product } = input;
      
      const { data, error } = await supabase
        .from('products')
        .insert(product)
        .select('*, category:categories(*)')
        .single();

      if (error) throw error;
      
      // Create inventory items if tracking is enabled
      if (product.track_inventory && data) {
        if (inventory_levels && inventory_levels.length > 0) {
          // New path: per-location inventory levels
          const inventoryRows = inventory_levels.map((level) => {
            const foh = level.quantity_foh ?? 0;
            const boh = level.quantity_boh ?? 0;
            return {
              product_id: data.id,
              location_id: level.location_id,
              quantity_foh: foh,
              quantity_boh: boh,
              quantity_on_hand: foh + boh,
              reorder_point: level.reorder_point ?? product.low_stock_threshold,
              reorder_quantity: level.reorder_quantity ?? null,
            };
          });

          const { error: inventoryError } = await supabase
            .from('inventory_items')
            .insert(inventoryRows);

          if (inventoryError) {
            console.error('Failed to create inventory items:', inventoryError);
          }

          // Fetch the created inventory items to get their IDs for transactions
          const { data: createdItems } = await supabase
            .from('inventory_items')
            .select('id, location_id, quantity_foh, quantity_boh')
            .eq('product_id', data.id);

          if (createdItems) {
            const txns: Array<{
              inventory_item_id: string;
              transaction_type: string;
              quantity_change: number;
              quantity_before: number;
              quantity_after: number;
              notes: string;
            }> = [];

            for (const item of createdItems) {
              if (item.quantity_foh > 0) {
                txns.push({
                  inventory_item_id: item.id,
                  transaction_type: 'received',
                  quantity_change: item.quantity_foh,
                  quantity_before: 0,
                  quantity_after: item.quantity_foh,
                  notes: 'Initial FOH stock on product creation',
                });
              }
              if (item.quantity_boh > 0) {
                txns.push({
                  inventory_item_id: item.id,
                  transaction_type: 'received_boh',
                  quantity_change: item.quantity_boh,
                  quantity_before: 0,
                  quantity_after: item.quantity_boh,
                  notes: 'Initial BOH stock on product creation',
                });
              }
            }

            if (txns.length > 0) {
              await supabase.from('inventory_transactions').insert(txns);
            }
          }
        } else {
          // Legacy path: single location with initial_stock (defaults to FOH)
          const qty = initial_stock ?? 0;
          const { data: inventoryData, error: inventoryError } = await supabase
            .from('inventory_items')
            .insert({
              product_id: data.id,
              location_id: product.location_id,
              quantity_on_hand: qty,
              quantity_foh: qty,
              quantity_boh: 0,
              reorder_point: product.low_stock_threshold,
            })
            .select('id')
            .single();

          if (inventoryError) {
            console.error('Failed to create inventory item:', inventoryError);
          }

          // Record initial stock transaction
          if (inventoryData && qty > 0) {
            await supabase.from('inventory_transactions').insert({
              inventory_item_id: inventoryData.id,
              transaction_type: 'received',
              quantity_change: qty,
              quantity_before: 0,
              quantity_after: qty,
              notes: 'Initial stock on product creation',
            });
          }
        }
      }
      
      return data as ProductWithCategory;
    },
    onSuccess: () => {
      // Invalidate all product queries broadly to ensure new products appear
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', locationId] });
      queryClient.invalidateQueries({ queryKey: ['products-with-supplier'] });
    },
  });

  // Update product
  const updateProduct = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateProductInput }) => {
      const { data, error } = await supabase
        .from('products')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*, category:categories(*)')
        .single();

      if (error) throw error;
      return data as ProductWithCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', locationId] });
      queryClient.invalidateQueries({ queryKey: ['products-with-supplier'] });
    },
  });

  // Toggle 86'd status
  const toggle86d = useMutation({
    mutationFn: async ({ id, is_available }: { id: string; is_available: boolean }) => {
      const { data, error } = await supabase
        .from('products')
        .update({ is_available, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', locationId] });
    },
  });

  // Delete product (soft delete)
  const deleteProduct = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('products')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', locationId] });
    },
  });

  // Create category
  const createCategory = useMutation({
    mutationFn: async (category: CreateCategoryInput) => {
      const { data, error } = await supabase
        .from('categories')
        .insert(category)
        .select()
        .single();

      if (error) throw error;
      return data as Category;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', locationId] });
    },
  });

  // Update category
  const updateCategory = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateCategoryInput }) => {
      const { data, error } = await supabase
        .from('categories')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Category;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', locationId] });
    },
  });

  // Delete category (soft delete)
  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('categories')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', locationId] });
    },
  });

  // Reorder categories
  const reorderCategories = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const updates = orderedIds.map((id, index) => ({
        id,
        sort_order: index,
        updated_at: new Date().toISOString(),
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('categories')
          .update({ sort_order: update.sort_order, updated_at: update.updated_at })
          .eq('id', update.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', locationId] });
    },
  });

  // Set product visibility for a location
  const setProductVisibility = useMutation({
    mutationFn: async ({ 
      productId, 
      targetLocationId, 
      isVisible 
    }: { 
      productId: string; 
      targetLocationId: string;
      isVisible: boolean;
    }) => {
      // Check if visibility record exists
      const { data: existing } = await supabase
        .from('product_location_visibility')
        .select('id')
        .eq('product_id', productId)
        .eq('location_id', targetLocationId)
        .single();

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('product_location_visibility')
          .update({ is_visible: isVisible })
          .eq('product_id', productId)
          .eq('location_id', targetLocationId);

        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('product_location_visibility')
          .insert({
            product_id: productId,
            location_id: targetLocationId,
            is_visible: isVisible,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-visibility'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  // Bulk set visibility for multiple products
  const bulkSetProductVisibility = useMutation({
    mutationFn: async ({ 
      productIds, 
      targetLocationId, 
      isVisible 
    }: { 
      productIds: string[]; 
      targetLocationId: string;
      isVisible: boolean;
    }) => {
      // Delete existing visibility settings for these products
      await supabase
        .from('product_location_visibility')
        .delete()
        .eq('location_id', targetLocationId)
        .in('product_id', productIds);

      // Insert new visibility settings
      const records = productIds.map(productId => ({
        product_id: productId,
        location_id: targetLocationId,
        is_visible: isVisible,
      }));

      const { error } = await supabase
        .from('product_location_visibility')
        .insert(records);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-visibility'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  // Realtime: subscribe to products and inventory changes so Can Sell / availability updates live
  useEffect(() => {
    if (!locationId || allLocations) return;

    const channels = [
      // Re-fetch products when any product row changes (e.g. is_active toggle, price update)
      subscribeToProducts(locationId, () => {
        queryClient.invalidateQueries({ queryKey: ['products'] });
      }),
      // Re-fetch products when inventory changes (updates Can Sell for composites)
      subscribeToInventory(locationId, () => {
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['composite-virtual-inventory'] });
      }),
    ];

    return () => {
      channels.forEach(ch => unsubscribe(ch));
    };
  }, [locationId, allLocations, queryClient]);

  return {
    // Products
    products: products ?? [],
    isLoading,
    error,
    refetch,
    createProduct,
    updateProduct,
    toggle86d,
    deleteProduct,
    // Categories
    categories: categories ?? [],
    categoriesLoading,
    refetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    // Location
    locationId,
    // Visibility
    visibilitySettings: visibilitySettings ?? [],
    setProductVisibility,
    bulkSetProductVisibility,
  };
}
